import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Loader2, GitBranch, Search, RotateCw, Check, X, Inbox, AlertCircle, Sparkles, ChevronRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/replay — Lead replay debugging tool.
 *
 * Re-runs a stored lead through the CURRENT routing engine in dry-run mode.
 * Shows every campaign that was evaluated, which criteria passed/failed,
 * who would win right now, and whether the winning buyer would actually be
 * eligible (wallet, business hours, cap, paused state).
 *
 * Zero side effects. The most powerful debugging weapon in the platform.
 */
export default function AdminReplay() {
  const [recent, setRecent] = useState(null);
  const [search, setSearch] = useState("");
  const [activeLead, setActiveLead] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/admin/replay/recent?limit=50")
      .then(r => setRecent(r.data?.items || []))
      .catch(() => toast.error("Could not load recent leads"));
  }, []);

  const run = async (leadId) => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post(`/admin/replay/${leadId}`, { overrides: {} });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replay failed");
    } finally {
      setRunning(false);
    }
  };

  const runById = async () => {
    if (!search.trim()) return;
    setActiveLead({ id: search.trim() });
    await run(search.trim());
  };

  const filtered = (recent || []).filter(r =>
    !search || r.id?.includes(search) || (r.email || "").includes(search) || (r.sector || "").includes(search)
  );

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Lead replay"
        subtitle="Re-run any past lead through the current routing engine in dry-run mode. Zero side effects — your favourite debugging weapon."
      />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LEFT: lead picker */}
        <Card className="border-slate-200 lg:col-span-1">
          <CardContent className="p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Pick a lead</div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="size-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <Input
                  className="h-9 pl-8 text-[13px]"
                  placeholder="Lead id / email / sector"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="replay-search"
                />
              </div>
              <Button size="sm" onClick={runById} disabled={!search.trim() || running} className="bg-slate-900 hover:bg-slate-800 h-9" data-testid="replay-run-id">
                Replay
              </Button>
            </div>

            <div className="space-y-1.5 max-h-[64vh] overflow-y-auto -mx-1 px-1">
              {!recent && <div className="text-center py-12 text-sm text-slate-500"><Loader2 className="size-4 inline animate-spin mr-2"/>Loading…</div>}
              {recent && filtered.length === 0 && (
                <div className="text-center py-12 text-sm text-slate-500">
                  <Inbox className="size-7 text-slate-300 mx-auto mb-2"/>
                  No leads {recent.length ? "match the search" : "yet"}.
                </div>
              )}
              {filtered.map(l => (
                <button
                  key={l.id}
                  onClick={() => { setActiveLead(l); run(l.id); }}
                  className={`w-full text-left rounded-md border p-2.5 transition-colors ${activeLead?.id === l.id ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  data-testid={`replay-lead-${l.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[12.5px] font-medium text-slate-900 truncate">{l.email || l.first_name || l.id.slice(0,8)}</div>
                    <Badge variant="outline" className="text-[9.5px] uppercase tracking-wide font-mono border-slate-200 bg-slate-50 text-slate-600">
                      {l.sector || "—"}
                    </Badge>
                  </div>
                  <div className="mt-1 text-[10.5px] text-slate-400 font-mono">
                    {new Date(l.created_at).toLocaleString("en-GB")} · {l.routing_via || "?"} · {l.status || "?"}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* RIGHT: result */}
        <div className="lg:col-span-2">
          {!result && !running && (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="py-20 text-center">
                <GitBranch className="size-10 text-slate-300 mx-auto" />
                <h3 className="mt-3 text-sm font-semibold text-slate-900">Pick a lead to replay</h3>
                <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
                  Re-runs the lead through the current routing engine using the live campaigns + criteria + ping trees. No wallet debit, no publisher payout, no webhook fired.
                </p>
              </CardContent>
            </Card>
          )}

          {running && (
            <Card className="border-slate-200">
              <CardContent className="py-16 text-center text-sm text-slate-500">
                <Loader2 className="size-5 inline animate-spin mr-2"/> Re-evaluating routing…
              </CardContent>
            </Card>
          )}

          {result && !running && <ReplayResult result={result} onRerun={() => run(activeLead?.id)} />}
        </div>
      </div>
    </div>
  );
}

function ReplayResult({ result, onRerun }) {
  const winner = result.winner;
  const elig = result.eligibility;

  return (
    <div className="space-y-4" data-testid="replay-result">
      {/* Summary card */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Replay result</div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {winner ? (
                  <>
                    <Sparkles className="size-4 text-emerald-600"/>
                    <span className="text-sm font-semibold text-slate-900 tracking-tight">
                      Would route to <span className="font-mono">{winner.campaign_name || winner.campaign_id}</span>
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="size-4 text-amber-600"/>
                    <span className="text-sm font-semibold text-slate-900 tracking-tight">No campaign would accept this lead right now</span>
                  </>
                )}
              </div>
              <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 flex-wrap font-mono">
                <span>sector · {result.sector}</span>
                <span className="text-slate-300">·</span>
                <span>{result.candidates} candidate{result.candidates === 1 ? "" : "s"}</span>
                <span className="text-slate-300">·</span>
                <span>{new Date(result.evaluated_at).toLocaleTimeString("en-GB")}</span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="border-slate-200" onClick={onRerun} data-testid="replay-rerun">
              <RotateCw className="size-3.5 mr-1.5"/> Re-run
            </Button>
          </div>

          {/* Eligibility outcome */}
          {winner && elig && (
            <div className={`mt-4 rounded-sm border p-3 flex items-start gap-2.5 ${elig.ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}>
              {elig.ok ? <Check className="size-4 text-emerald-600 shrink-0 mt-0.5"/> : <AlertCircle className="size-4 text-amber-600 shrink-0 mt-0.5"/>}
              <div className="text-[12.5px] leading-snug">
                {elig.ok ? (
                  <>Buyer <span className="font-mono font-semibold">{elig.client_id?.slice(0,8)}…</span> is currently eligible. Would charge <span className="font-semibold">£{(elig.would_charge_gbp || 0).toLocaleString()}</span>.</>
                ) : (
                  <>Criteria passed but buyer would be REJECTED at eligibility check: <span className="font-mono font-semibold">{elig.reason || "unknown"}</span></>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-campaign cascade */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900 tracking-tight">Campaign cascade</h3>
            <p className="text-[11.5px] text-slate-500 mt-0.5">Walked newest-first. First criteria-passing campaign wins.</p>
          </div>
          {result.trace.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              No live campaigns exist for sector <span className="font-mono">{result.sector}</span>.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {result.trace.map((t, i) => (
                <CascadeRow key={i} t={t} index={i} isWinner={i === result.trace.findIndex(x => x.criteria_pass)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Lead payload */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900 tracking-tight">Lead payload (used for matching)</h3>
          </div>
          <pre className="text-[11.5px] text-slate-700 font-mono leading-relaxed p-5 max-h-72 overflow-auto bg-slate-50/60">
            {JSON.stringify(result.payload, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

function CascadeRow({ t, index, isWinner }) {
  return (
    <li className={`px-5 py-3.5 ${isWinner ? "bg-emerald-50/30" : ""}`} data-testid={`cascade-${index}`}>
      <div className="flex items-center gap-3">
        <span className={`inline-flex size-6 items-center justify-center rounded-sm text-[10px] font-mono font-semibold ${isWinner ? "bg-emerald-600 text-white" : t.criteria_pass ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-400"}`}>
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 truncate">{t.campaign_name || t.campaign_id}</span>
            {t.criteria_pass
              ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wide font-medium">passed</Badge>
              : <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-[10px] uppercase tracking-wide font-medium">skipped</Badge>}
            {isWinner && <Badge variant="outline" className="border-emerald-400 bg-emerald-600 text-white text-[10px] uppercase tracking-wide font-medium gap-1"><Sparkles className="size-2.5"/> winner</Badge>}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 font-mono">
            sectors: {(t.sectors || []).join(", ") || "—"} · {t.rules?.length || 0} rule{t.rules?.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {(t.rules || []).length > 0 && (
        <div className="mt-2.5 ml-9 space-y-1">
          {t.rules.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px]">
              {r.passed
                ? <Check className="size-3 text-emerald-600 shrink-0" strokeWidth={2.5}/>
                : <X className="size-3 text-rose-600 shrink-0" strokeWidth={2.5}/>}
              <span className={`font-mono ${r.passed ? "text-slate-600" : "text-slate-900 font-medium"}`}>
                {r.field} {prettyOp(r.op)} {Array.isArray(r.value) ? r.value.join(", ") : String(r.value ?? "")}
              </span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function prettyOp(op) {
  return ({
    equals: "=", not_equals: "≠",
    in: "∈", not_in: "∉",
    gte: "≥", lte: "≤", gt: ">", lt: "<",
    exists: "exists", not_exists: "missing",
  })[op] || op;
}
